import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Alert,
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Divider,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  InputLabel,
  Tooltip,
  TableSortLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  SwapHoriz as SwapIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

import DeviceCard from '../components/DeviceCard';
import useRelativeTime from '../hooks/useRelativeTime';
import { fetchGPSData, refreshGPSData, fetchGpsSystems, updateGpsSystem, createGpsSystem, deleteGpsSystem, fetchRegions, fetchBusinessUnits, fetchBusinessUnitForRegion, switchGpsSystemProvider } from '../services/api';
import SwitchProviderDialog, { PROVIDER_LABEL, otherProvider } from '../components/SwitchProviderDialog';
import { useAuth, canEditEquipment } from '../context/AuthContext';

const RECEIVER_MODELS = ['BX992', 'SV100'];

// Rows saved before the dropdown switched from brand to model name. Mapped on
// read so existing cards still match an option; they rewrite to the new value
// on their next save.
const LEGACY_RECEIVER_MODELS = { Trimble: 'BX992', SingularXYZ: 'SV100' };
const normalizeReceiverModel = (v) => LEGACY_RECEIVER_MODELS[v] || v || '';

// The SV100 has the cellular modem built in, so it has no separate router and
// its IMEI is the unit's own.
const isSv100 = (model) => model === 'SV100';

const EMPTY_ADD_FORM = {
  gps_number: '',
  receiver_model: '',
  serial: '',
  machine_assignment: '',
  router_model: '',
  router_imei: '',
  router_sim: '',
  ip_address: '',
  firmware_version: '',
  region: 'General',
  biz_unit: '',
};

const EMPTY_FORM = {
  machine_assignment: '',
  receiver_model: '',
  serial: '',
  router_model: '',
  router_imei: '',
  router_sim: '',
  ip_address: '',
  firmware_version: '',
  region: '',
  biz_unit: '',
  ntrip_server: '',
  ntrip_username: '',
  ntrip_password: '',
  ntrip_mountpoint: '',
  linked_license_id: '',
};

function Dashboard() {
  const { user } = useAuth();
  const canEdit = canEditEquipment(user?.role);
  const canAddUnit = ['admin', 'technician'].includes(user?.role);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // Age of the snapshot currently displayed. The poller stamps last_update in
  // UTC ('YYYY-MM-DD HH:MM:SS'); normalize to ISO with a Z so the browser
  // parses it as UTC instead of local time. useRelativeTime's shared ticker
  // also keeps this re-evaluating, so the banner appears/clears on its own.
  const snapshotIso = data?.last_update ? data.last_update.replace(' ', 'T') + 'Z' : null;
  const snapshotAge = useRelativeTime(snapshotIso);
  const snapshotDelayed = snapshotIso ? Date.now() - Date.parse(snapshotIso) > 120000 : false;
  const [gpsSort, setGpsSort] = useState(null); // null | 'asc' | 'desc'
  const [businessUnits, setBusinessUnits] = useState([]);

  const handleGpsSortClick = () => {
    setGpsSort(prev => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : null));
  };

  const [detailsRecords, setDetailsRecords] = useState([]);
  const [popupDevice, setPopupDevice] = useState(null);
  // Live "x ago" for the popup map's Last Update banner, matching DeviceCard:
  // fall back to the server-rendered string only when the ISO field is absent.
  const popupLiveUpdate = useRelativeTime(popupDevice?.last_update_iso);
  const popupLastUpdate = popupDevice?.last_update_iso === undefined
    ? (popupDevice?.last_update || 'Never')
    : popupLiveUpdate;
  const [popupForm, setPopupForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [switchOpen, setSwitchOpen] = useState(false);
  const [switchNotice, setSwitchNotice] = useState(null); // { severity, text } after a provider switch

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_ADD_FORM);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');

  const [search, setSearch] = useState('');
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeId, setRemoveId] = useState('');
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');

  const loadData = useCallback(async () => {
    try {
      const result = await fetchGPSData();
      // Monotonic guard: a web worker serving from a stuck cache can answer with
      // a snapshot HOURS older than one we already rendered (observed 2026-07-31:
      // refreshes alternated between a fresh payload and a 4h-old one depending
      // on which worker responded, making devices "randomly" die and revive).
      // Never replace newer data with older; the stamp format sorts lexically.
      setData(prev => (
        prev?.last_update && result?.last_update && result.last_update < prev.last_update
          ? prev
          : result
      ));
    } catch (error) {
      console.error('Error fetching GPS data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetails = useCallback(async () => {
    try {
      const result = await fetchGpsSystems();
      setDetailsRecords(result.records || []);
    } catch (err) {
      console.error('Error fetching GPS details:', err);
    }
  }, []);

  const handleRemove = async () => {
    if (!removeId) return;
    setRemoving(true);
    setRemoveError('');
    try {
      await deleteGpsSystem(removeId);
      await Promise.all([loadDetails(), loadData()]);
      setRemoveOpen(false);
      setRemoveId('');
    } catch (err) {
      setRemoveError(err.message || 'Remove failed');
    } finally {
      setRemoving(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const result = await refreshGPSData();
      setData(result);
    } catch (error) {
      console.error('Error refreshing GPS data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    loadDetails();
    fetchRegions(undefined, user?.country).then(d => setRegions(d.regions || [])).catch(() => {});
    fetchBusinessUnits(user?.country).then(d => setBusinessUnits(d.businessUnits || [])).catch(() => {});
  }, [loadData, loadDetails, user?.country]);

  // Background polling is always on (the auto-refresh toggle was retired)
  useEffect(() => {
    const interval = setInterval(() => { loadData(); }, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleCardClick = (device) => {
    const record = detailsRecords.find(r => r.gps_number === device.gps_num) || {};
    setPopupForm({
      machine_assignment: record.machine_assignment || '',
      receiver_model: normalizeReceiverModel(record.receiver_model),
      serial: record.serial || '',
      router_model: record.router_model || '',
      router_imei: record.router_imei || '',
      router_sim: record.router_sim || '',
      ip_address: record.ip_address || '',
      firmware_version: record.firmware_version || '',
      region: record.region || '',
      biz_unit: record.biz_unit || '',
      ntrip_server: record.ntrip_server || '',
      ntrip_username: record.ntrip_username || '',
      ntrip_password: record.ntrip_password || '',
      ntrip_mountpoint: record.ntrip_mountpoint || '',
      linked_license_id: record.linked_license_id || '',
    });
    // Refresh regions list scoped to this GPS's BU so the dropdown only shows valid options
    fetchRegions(record.biz_unit || undefined, user?.country)
      .then(d => setRegions(d.regions || []))
      .catch(() => {});
    setSaveError('');
    setPopupDevice(device);
  };

  // If we arrived via ?gps=GPSxxx (e.g. from the Machine Licenses tab), auto-open that GPS's popup
  // once BOTH the live device data and the GPS Systems detail records have loaded — otherwise the
  // popup opens with an empty form because handleCardClick reads from detailsRecords.
  useEffect(() => {
    const gpsParam = searchParams.get('gps');
    if (!gpsParam || !data?.results || !detailsRecords?.length || popupDevice) return;
    const device = data.results.find(d => d.gps_num === gpsParam);
    if (device) {
      handleCardClick(device);
      // Drop the query param so a manual close doesn't immediately re-trigger
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, data, detailsRecords]);

  const handleClose = () => {
    setPopupDevice(null);
    setSaveError('');
    setSwitchNotice(null);
  };

  // Same caster-address rule as the provider logo in the NTRIP section.
  const popupProvider = (() => {
    const server = (popupForm.ntrip_server || '').toLowerCase();
    if (server.includes('pointone')) return 'pointone';
    if (server.includes('premium') || server.includes('rtkpremium')) return 'rtkpremium';
    return null;
  })();

  const handleSwitchProvider = async () => {
    const res = await switchGpsSystemProvider(popupDevice.gps_num, user?.fullName || user?.username || '');
    // Save sends the whole form, so it must carry the new credentials, not the old ones.
    setPopupForm(prev => ({
      ...prev,
      ntrip_server: res.credentials.server,
      ntrip_username: res.credentials.username,
      ntrip_password: res.credentials.password,
      ntrip_mountpoint: res.credentials.mountpoint,
      linked_license_id: res.license_id,
    }));
    setSwitchNotice(res.warning
      ? { severity: 'warning', text: res.warning }
      : { severity: 'success', text: `Switched to ${PROVIDER_LABEL[res.provider]}. Enter the new credentials on the receiver.` });
    loadDetails();
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await updateGpsSystem(popupDevice.gps_num, popupForm);
      await loadDetails();
      // Trigger a live refresh so a newly-added IP appears as a live card
      // immediately instead of waiting for the next 30 s polling tick.
      try {
        const refreshed = await refreshGPSData();
        setData(refreshed);
      } catch { /* non-fatal; auto-refresh will catch up */ }
      handleClose();
    } catch (err) {
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field) => (e) =>
    setPopupForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleAddOpen = () => {
    // Prefill the first unused GPS number (gaps from removed units get reused;
    // was max+1 until 2026-08 — the user prefers filling gaps).
    const used = new Set();
    for (const r of detailsRecords) {
      const m = /^GPS(\d+)$/i.exec((r.gps_number || '').trim());
      if (m) used.add(parseInt(m[1], 10));
    }
    let next = 1;
    while (used.has(next)) next++;
    setAddForm({ ...EMPTY_ADD_FORM, gps_number: `GPS${String(next).padStart(3, '0')}` });
    setAddError('');
    setAddOpen(true);
  };

  const handleAddClose = () => {
    setAddOpen(false);
    setAddError('');
  };

  const handleAddChange = (field) => (e) =>
    setAddForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleAddSave = async () => {
    if (!addForm.gps_number.trim()) {
      setAddError('GPS number is required');
      return;
    }
    setAddSaving(true);
    setAddError('');
    try {
      await createGpsSystem({ ...addForm, gps_number: addForm.gps_number.trim() });
      await loadDetails();
      // If an IP was provided, poll it right away so the unit shows live
      try {
        const refreshed = await refreshGPSData();
        setData(refreshed);
      } catch { /* non-fatal; auto-refresh will catch up */ }
      handleAddClose();
    } catch (err) {
      setAddError(err.message || 'Failed to add unit');
    } finally {
      setAddSaving(false);
    }
  };

  // Live devices first (worst health first), then the states needing attention:
  // a login failure, then a fresh dropout. Grading the offline side by outage
  // kind rather than by leftover issue flags keeps the recently-dropped units —
  // the actionable ones — above long-dead hardware. Sustained outages and
  // not-yet-configured units are both parked states, so they share the last
  // bucket and read in GPS-number order.
  const PARKED_SCORE = 5;
  const getSortScore = (device) => {
    if (device._placeholder) return PARKED_SCORE;
    const critical = ['GPS_SATS', 'NTRIP_CONFIG', 'NTRIP_ACCOUNT'];
    const hasCritical = device.issues?.some(i => critical.includes(i.type));
    if (device.online && hasCritical) return 0;
    if (device.online && device.has_issue) return 1;
    if (device.online) return 2;
    if (device.status === 'auth_failed') return 3;
    if (device.offline_kind === 'unreachable') return 4;
    return PARKED_SCORE;
  };

  // Within the fresh-dropout bucket, most recently seen first; never-seen last.
  const lastSeenMs = (device) => {
    const ms = device.last_update_iso ? Date.parse(device.last_update_iso) : NaN;
    return Number.isNaN(ms) ? -Infinity : ms;
  };

  const gpsNumber = (device) => parseInt((device.gps_num || '').replace(/\D/g, ''), 10) || 0;

  const formatIssues = (device) => {
    if (!device?.has_issue || !device?.issues?.length) return '';
    return device.issues.map(i => i.message || i.status).filter(Boolean).join('\n');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box>
      {/* The poller stamps every snapshot; if what we're showing was generated
          more than 2 minutes ago the viewer must know they're looking at the
          past. A stuck web-worker cache once served a 4-hour-old snapshot as
          current (2026-07-31) — this banner is what was missing that day. */}
      {snapshotDelayed && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Live data delayed — the last poller update was {snapshotAge} ago.
          Device statuses below may be out of date.
        </Alert>
      )}

      {/* Tabs — match the rovers page */}
      <Tabs
        value={0}
        onChange={(_, v) => { if (v === 1) navigate('/equipment/machines/gps-health/history'); }}
        sx={{ mb: 0.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Devices" />
        <Tab label="History" />
      </Tabs>

      {/* Controls Row */}
      <Grid container spacing={2} sx={{ mb: 3, mt: 0 }}>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', height: '100%' }}>
            {/* Devices Online — compact inline stat (was a full StatsCard) */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                {`${data?.online_count || 0}/${data?.total_count || 0}`}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Devices Online
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              height: '100%',
            }}
          >
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search devices..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: 260,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                  '& fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                  '&:hover fieldset': { borderColor: 'primary.dark' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
            {canAddUnit && (
              <>
                <Tooltip title="Add GPS" arrow>
                  <IconButton
                    onClick={handleAddOpen}
                    sx={{ backgroundColor: 'secondary.main', color: 'white', '&:hover': { backgroundColor: 'secondary.dark' }, width: 36, height: 36 }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove GPS" arrow>
                  <IconButton
                    onClick={() => { setRemoveId(''); setRemoveError(''); setRemoving(false); setRemoveOpen(true); }}
                    sx={{ backgroundColor: 'error.main', color: 'white', '&:hover': { backgroundColor: 'error.dark' }, width: 36, height: 36 }}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Table Header */}
      <Paper
        sx={{
          display: { xs: 'none', md: 'block' },
          mb: 0.75, px: 2, py: 1,
          backgroundColor: 'secondary.main',
          color: 'white',
          borderRadius: 1,
        }}
      >
        <Grid container spacing={2} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
          <Grid item sx={{ flexShrink: 0, width: 80 }}>
            <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</Typography>
          </Grid>
          <Grid item sx={{ flexShrink: 0, width: 140 }}>
            <TableSortLabel
              active={!!gpsSort}
              direction={gpsSort || 'asc'}
              onClick={handleGpsSortClick}
              sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' }, ml: 3 }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>GPS</Typography>
            </TableSortLabel>
          </Grid>
          <Grid item sx={{ flexShrink: 0, width: 200 }}>
            <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Machine</Typography>
          </Grid>
          <Grid item sx={{ flexShrink: 0, width: 140 }}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Receiver</Typography>
          </Grid>
          <Grid item sx={{ flexShrink: 0, width: 220 }}>
            <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>IP</Typography>
          </Grid>
          <Grid item sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Details</Typography>
          </Grid>
          <Grid item sx={{ flexShrink: 0, width: 160, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <Tooltip title="Refresh now" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  sx={{ color: 'white', p: 0.25, '&.Mui-disabled': { color: 'rgba(255,255,255,0.5)' } }}
                >
                  {refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Last Update</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Device Cards. Placeholders are synthesized for GpsSystems rows whose IP
          isn't filled in yet — they don't get polled but appear on the list so
          an admin can click in and add the IP. */}
      {(() => {
        const liveGpsNums = new Set((data?.results || []).map(d => d.gps_num));
        const placeholderDevices = (detailsRecords || [])
          .filter(r => r.gps_number && !liveGpsNums.has(r.gps_number))
          .map(r => ({
            gps_num: r.gps_number,
            machine: r.machine_assignment || '',
            ip: '',
            online: false,
            type: '',
            has_issue: false,
            issues: [],
            last_update: '',
            _placeholder: true,
          }));
        // Receiver model lives on the GpsSystems record, not the poller snapshot — join it in.
        const receiverByGps = Object.fromEntries((detailsRecords || []).map(r => [r.gps_number, r.receiver_model || '']));
        const term = search.trim().toLowerCase();
        return [...(data?.results || []), ...placeholderDevices]
          .map(d => ({ ...d, receiver_model: receiverByGps[d.gps_num] || '' }))
          .filter(d =>
            !term
            || (d.gps_num || '').toLowerCase().includes(term)
            || (d.machine || '').toLowerCase().includes(term)
            || (d.receiver_model || '').toLowerCase().includes(term)
            || (d.ip || '').toLowerCase().includes(term)
          );
      })().sort((a, b) => {
        if (gpsSort) {
          const diff = gpsNumber(a) - gpsNumber(b);
          return gpsSort === 'asc' ? diff : -diff;
        }
        const score = getSortScore(a);
        const byScore = score - getSortScore(b);
        if (byScore !== 0) return byScore;
        // Offline + not-yet-configured units read in GPS-number order
        if (score === PARKED_SCORE) return gpsNumber(a) - gpsNumber(b);
        // Fresh dropouts: a unit that dropped hours ago outranks one dead for a
        // month, "Never" last. Compared rather than subtracted, since two
        // never-seen units are both -Infinity.
        if (!a.online && !b.online) {
          const aSeen = lastSeenMs(a), bSeen = lastSeenMs(b);
          if (aSeen !== bSeen) return bSeen > aSeen ? 1 : -1;
        }
        return 0;
      }).map((device) => (
        <DeviceCard
          key={device.gps_num}
          device={device}
          onClick={() => handleCardClick(device)}
        />
      ))}

      {(!data?.results || data.results.length === 0) && (!detailsRecords || detailsRecords.length === 0) && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No GPS devices found.</Typography>
        </Paper>
      )}

      {/* Device Detail Popup */}
      <Dialog open={Boolean(popupDevice)} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h5" fontWeight={700}>{popupDevice?.gps_num}</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>

          {/* Top section: info left, map right */}
          <Grid container spacing={3}>

            {/* Left column */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                {/* Device fields */}
                {[
                  { label: 'Machine', field: 'machine_assignment' },
                  { label: 'Serial', field: 'serial' },
                ].map(({ label, field }) => (
                  <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ width: 100, flexShrink: 0 }}>{label}</Typography>
                    {canEdit ? (
                      <TextField size="small" fullWidth value={popupForm[field]} onChange={handleChange(field)} />
                    ) : (
                      <Typography variant="body2" fontWeight={500}>{popupForm[field] || '—'}</Typography>
                    )}
                  </Box>
                ))}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 100, flexShrink: 0 }}>Receiver</Typography>
                  {canEdit ? (
                    <FormControl size="small" fullWidth>
                      <Select
                        value={popupForm.receiver_model}
                        displayEmpty
                        renderValue={(v) => v || <em>Select receiver model</em>}
                        onChange={(e) => {
                          const model = e.target.value;
                          setPopupForm(prev => ({ ...prev, receiver_model: model, ...(isSv100(model) ? { router_model: '' } : {}) }));
                        }}
                      >
                        {RECEIVER_MODELS.map((b) => (
                          <MenuItem key={b} value={b}>{b}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body2" fontWeight={500}>{popupForm.receiver_model || '—'}</Typography>
                  )}
                </Box>

                {[
                  ...(isSv100(popupForm.receiver_model) ? [] : [{ label: 'Router Model', field: 'router_model' }]),
                  { label: isSv100(popupForm.receiver_model) ? 'SV100 IMEI' : 'Router IMEI', field: 'router_imei' },
                  { label: 'SIM', field: 'router_sim' },
                  { label: 'IP Address', field: 'ip_address' },
                  { label: 'Firmware', field: 'firmware_version' },
                ].map(({ label, field }) => (
                  <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ width: 100, flexShrink: 0 }}>{label}</Typography>
                    {canEdit ? (
                      <TextField size="small" fullWidth value={popupForm[field]} onChange={handleChange(field)} />
                    ) : (
                      <Typography variant="body2" fontWeight={500}>{popupForm[field] || '—'}</Typography>
                    )}
                  </Box>
                ))}

                {/* Current issues */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 100, flexShrink: 0, pt: 1 }}>Issues</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={formatIssues(popupDevice) || 'No issues detected'}
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: popupDevice?.has_issue ? 'error.main' : 'success.main',
                      },
                    }}
                  />
                </Box>

              </Box>
            </Grid>

            {/* Right column — map + region */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {(() => {
                  const lat = parseFloat(popupDevice?.lat);
                  const lon = parseFloat(popupDevice?.lon);
                  const hasPosition = !isNaN(lat) && !isNaN(lon);
                  return hasPosition ? (
                    <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden', height: 320, border: 1, borderColor: 'divider' }}>
                      {/* Small pill in the bottom-left corner so the shading only covers
                          its own text, not a strip of the map. Sits above the Leaflet
                          panes (z-index 400-700) but below its controls (1000). */}
                      <Box sx={{
                        position: 'absolute', bottom: 8, left: 8, zIndex: 900,
                        px: 1, py: 0.25, borderRadius: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.55)', color: '#fff',
                        pointerEvents: 'none',
                      }}>
                        <Typography variant="caption">Last Update: {popupLastUpdate}</Typography>
                      </Box>
                      <MapContainer
                        key={popupDevice?.gps_num}
                        center={[lat, lon]}
                        zoom={13}
                        style={{ height: '100%', width: '100%' }}
                        attributionControl={true}
                      >
                        <TileLayer
                          url={isDark
                            ? 'https://tile.jawg.io/jawg-dark/{z}/{x}/{y}{r}.png?access-token=6FXDBzhanSef1HN4s1LZ3mPreirTHg2H5ErbrBakZuSCBpEww5wULxbgjz9EzrE6'
                            : 'https://tile.jawg.io/jawg-streets/{z}/{x}/{y}{r}.png?access-token=6FXDBzhanSef1HN4s1LZ3mPreirTHg2H5ErbrBakZuSCBpEww5wULxbgjz9EzrE6'
                          }
                          attribution='<a href="https://www.jawg.io" target="_blank">&copy; Jawg Maps</a>'
                        />
                        <Marker position={[lat, lon]} />
                      </MapContainer>
                    </Box>
                  ) : (
                    <Box sx={{
                      height: 320,
                      border: 1, borderColor: 'divider', borderRadius: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Typography color="text.secondary" variant="body2">No position available</Typography>
                    </Box>
                  );
                })()}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 100, flexShrink: 0 }}>Business Unit</Typography>
                  {canEdit ? (
                    <FormControl size="small" fullWidth>
                      <Select
                        value={popupForm.biz_unit}
                        displayEmpty
                        onChange={(e) => {
                          const bu = e.target.value;
                          // Clear the region — the current value may not belong to the new BU.
                          setPopupForm(prev => ({ ...prev, biz_unit: bu, region: '' }));
                          fetchRegions(bu || undefined, user?.country)
                            .then(d => setRegions(d.regions || []))
                            .catch(() => {});
                        }}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {businessUnits.map((b) => (
                          <MenuItem key={b} value={b}>{b}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body2" fontWeight={500}>{popupForm.biz_unit || '—'}</Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 100, flexShrink: 0 }}>Region</Typography>
                  {canEdit ? (
                    <FormControl size="small" fullWidth>
                      <Select
                        value={popupForm.region}
                        displayEmpty
                        onChange={async (e) => {
                          const region = e.target.value;
                          setPopupForm(prev => ({ ...prev, region }));
                          if (region) {
                            try {
                              const data = await fetchBusinessUnitForRegion(region, user?.country);
                              setPopupForm(prev => ({ ...prev, biz_unit: data.businessUnit || prev.biz_unit }));
                            } catch {
                              /* leave biz_unit as-is */
                            }
                          }
                        }}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {[...regions, ...(!regions.includes('General') ? ['General'] : [])].map((r) => (
                          <MenuItem key={r} value={r}>{r}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body2" fontWeight={500}>{popupForm.region || '—'}</Typography>
                  )}
                </Box>
              </Box>
            </Grid>

          </Grid>

          {/* NTRIP Information — full width, read-only */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>NTRIP Information</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {canEdit && popupProvider && (
                  <Button size="small" variant="outlined" startIcon={<SwapIcon />} onClick={() => setSwitchOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                    Switch to {PROVIDER_LABEL[otherProvider(popupProvider)]}
                  </Button>
                )}
                {/* Provider logo (same art as the licenses page), derived from the caster URL */}
                {(() => {
                  const server = (popupForm.ntrip_server || '').toLowerCase();
                  if (server.includes('pointone')) {
                    return (
                      <Tooltip title="PointOneNav" arrow>
                        <Box sx={{
                          width: 32, height: 32, flexShrink: 0,
                          backgroundColor: '#418039',
                          WebkitMaskImage: 'url(/p1n_logo.png)',
                          WebkitMaskSize: 'contain',
                          WebkitMaskPosition: 'center',
                          WebkitMaskRepeat: 'no-repeat',
                          maskImage: 'url(/p1n_logo.png)',
                          maskSize: 'contain',
                          maskPosition: 'center',
                          maskRepeat: 'no-repeat',
                        }} />
                      </Tooltip>
                    );
                  }
                  if (server.includes('premium') || server.includes('rtkpremium')) {
                    return (
                      <Tooltip title="PremiumRTK" arrow>
                        <Box
                          component="img"
                          src="/premium_rtk_logo.png"
                          alt="Premium RTK"
                          sx={{
                            width: 32, height: 32, borderRadius: '50%',
                            objectFit: 'cover', flexShrink: 0,
                            backgroundColor: '#fff',
                          }}
                        />
                      </Tooltip>
                    );
                  }
                  return null;
                })()}
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {switchNotice && (
              <Alert severity={switchNotice.severity} onClose={() => setSwitchNotice(null)} sx={{ mb: 2 }}>
                {switchNotice.text}
              </Alert>
            )}
            <Grid container spacing={2}>
              {[
                { label: 'Server', value: popupForm.ntrip_server },
                { label: 'Port', value: popupForm.ntrip_server ? '2101' : '' },
                { label: 'Username', value: popupForm.ntrip_username },
                { label: 'Password', value: popupForm.ntrip_password },
                { label: 'Mountpoint', value: popupForm.ntrip_mountpoint },
              ].map(({ label, value }) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>{label}</Typography>
                    <TextField
                      size="small"
                      fullWidth
                      value={value || ''}
                      InputProps={{
                        readOnly: true,
                        endAdornment: value ? (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => navigator.clipboard.writeText(value)} sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

        </DialogContent>

        {saveError && (
          <Box sx={{ px: 3, pb: 1 }}>
            <Typography color="error" variant="body2">{saveError}</Typography>
          </Box>
        )}

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>{canEdit ? 'Cancel' : 'Close'}</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <SwitchProviderDialog
        open={switchOpen}
        unitName={popupDevice?.gps_num}
        provider={popupProvider}
        onClose={() => setSwitchOpen(false)}
        onConfirm={handleSwitchProvider}
      />

      {/* Add Unit Popup */}
      <Dialog open={addOpen} onClose={handleAddClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h5" fontWeight={700}>Add GPS Unit</Typography>
          <IconButton onClick={handleAddClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[
              { label: 'GPS Number', field: 'gps_number', required: true },
            ].map(({ label, field, required }) => (
              <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>
                  {label}{required ? ' *' : ''}
                </Typography>
                <TextField size="small" fullWidth value={addForm[field]} onChange={handleAddChange(field)} />
              </Box>
            ))}

            {[
              { label: 'Serial', field: 'serial' },
              { label: 'Machine', field: 'machine_assignment' },
            ].map(({ label, field, required }) => (
              <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>
                  {label}{required ? ' *' : ''}
                </Typography>
                <TextField size="small" fullWidth value={addForm[field]} onChange={handleAddChange(field)} />
              </Box>
            ))}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>Receiver Model</Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={addForm.receiver_model}
                  displayEmpty
                  renderValue={(v) => v || <em>Select receiver model</em>}
                  onChange={(e) => {
                    const model = e.target.value;
                    // The SV100 has no separate router, so drop any router model
                    // typed before the switch rather than saving it against the unit.
                    setAddForm(prev => ({ ...prev, receiver_model: model, ...(isSv100(model) ? { router_model: '' } : {}) }));
                  }}
                >
                  {RECEIVER_MODELS.map((b) => (
                    <MenuItem key={b} value={b}>{b}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {[
              ...(isSv100(addForm.receiver_model) ? [] : [{ label: 'Router Model', field: 'router_model' }]),
              { label: isSv100(addForm.receiver_model) ? 'SV100 IMEI' : 'Router IMEI', field: 'router_imei' },
              { label: 'SIM', field: 'router_sim' },
              { label: 'IP Address', field: 'ip_address' },
              { label: 'Firmware', field: 'firmware_version' },
            ].map(({ label, field, required }) => (
              <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>
                  {label}{required ? ' *' : ''}
                </Typography>
                <TextField size="small" fullWidth value={addForm[field]} onChange={handleAddChange(field)} />
              </Box>
            ))}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>Business Unit</Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={addForm.biz_unit}
                  displayEmpty
                  renderValue={(v) => v || <em>Select business unit</em>}
                  onChange={(e) => {
                    const bu = e.target.value;
                    // Clear the region — the current value may not belong to the new BU.
                    setAddForm(prev => ({ ...prev, biz_unit: bu, region: '' }));
                    fetchRegions(bu, user?.country)
                      .then(d => setRegions(d.regions || []))
                      .catch(() => {});
                  }}
                >
                  {businessUnits.map((b) => (
                    <MenuItem key={b} value={b}>{b}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>Region</Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={addForm.region}
                  displayEmpty
                  disabled={!addForm.biz_unit}
                  renderValue={(v) => v || <em>{addForm.biz_unit ? 'Select region' : 'Select business unit first'}</em>}
                  onChange={(e) => setAddForm(prev => ({ ...prev, region: e.target.value }))}
                >
                  {[...regions, ...(!regions.includes('General') ? ['General'] : [])].map((r) => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>

        {addError && (
          <Box sx={{ px: 3, pb: 1 }}>
            <Typography color="error" variant="body2">{addError}</Typography>
          </Box>
        )}

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleAddClose}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSave} disabled={addSaving}>
            {addSaving ? 'Adding...' : 'Add Unit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Unit Popup */}
      <Dialog open={removeOpen} onClose={() => setRemoveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove Unit</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Select the GPS system to remove</Typography>
          <FormControl fullWidth size="small">
            <InputLabel>GPS System</InputLabel>
            <Select value={removeId} label="GPS System" onChange={(e) => setRemoveId(e.target.value)}>
              {detailsRecords.map((r) => (
                <MenuItem key={r.gps_number} value={r.gps_number}>
                  {r.gps_number}{r.machine_assignment ? ` — ${r.machine_assignment}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {removeError && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{removeError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRemove} disabled={removing || !removeId}>
            {removing ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Dashboard;
